import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { AlertCircle, Check, CheckCircle2, Copy, Info, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// In-app toasts for transient acknowledgements ("Checkpoint renamed", "Fields
// synced") — the calm replacement for browser alert()-style feedback. For
// persistent, reviewable results (publish/send/restore summaries) the screens
// keep their inline Callouts; toasts are for quick confirmations.
//
// Errors are the exception: they're opaque + high-contrast, they DON'T
// auto-dismiss (a long diagnostic message — e.g. a raw D1 SQL error — must stay
// put long enough to read and copy), and they carry a copy button. Success/info
// stay light and auto-dismiss.

export type ToastTone = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  tone: ToastTone
  message: string
  durable: boolean
}

export interface ToastOptions {
  /** Keep the toast up longer and give it a Copy button.
   *
   *  For RESULT summaries rather than acknowledgements: a publish tally
   *  ("Published v4. 2 new, 0 changed, 0 withdrawn.") is a number an operator
   *  may need to read carefully, quote, or paste somewhere, and 4 seconds is
   *  not enough for that. "Saved" needs no such treatment. */
  durable?: boolean
}

export interface ToastApi {
  success: (message: string, opts?: ToastOptions) => void
  error: (message: string) => void
  info: (message: string, opts?: ToastOptions) => void
}

/** Ordinary acknowledgement: long enough to register, short enough to ignore. */
export const TOAST_BRIEF_MS = 4_000
/** A result worth reading and copying. */
export const TOAST_DURABLE_MS = 15_000

/** How long a toast stays up; `null` means "until dismissed".
 *
 *  Pure, and exported, so the timing POLICY is pinned by a test on its own terms
 *  rather than through whatever renders it. (Toast's rendered output is separately
 *  covered in Toast.test.tsx, since 2026-08-01.) */
export function toastDurationMs(tone: ToastTone, durable: boolean): number | null {
  // A long diagnostic (a raw D1 error) must stay put long enough to read and
  // copy, so errors never auto-dismiss regardless of `durable`.
  if (tone === 'error') return null
  return durable ? TOAST_DURABLE_MS : TOAST_BRIEF_MS
}

const ToastCtx = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

// All tones use an OPAQUE card (bg-surface) with a tinted icon, so a toast is
// always legible over whatever is behind it. No side stripe.
const TONE: Record<ToastTone, { icon: LucideIcon; iconColor: string }> = {
  success: { icon: CheckCircle2, iconColor: 'text-brand-green-dark' },
  error: { icon: AlertCircle, iconColor: 'text-brand-red-dark' },
  info: { icon: Info, iconColor: 'text-brand-maroon' },
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* clipboard blocked — selection still works */
      },
    )
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? 'Copied' : 'Copy message'}
      className="inline-flex items-center gap-1 rounded text-xs font-medium text-ink-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red-dark"
    >
      {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

interface ToastViewProps {
  item: ToastItem
  onClose: () => void
  onPause: () => void
  onResume: () => void
}

function ToastView({ item, onClose, onPause, onResume }: ToastViewProps) {
  // Hover and focus pause independently: the timer resumes only when neither holds.
  const held = useRef({ hover: false, focus: false })
  const hold = (key: 'hover' | 'focus', on: boolean) => {
    const was = held.current.hover || held.current.focus
    held.current[key] = on
    const now = held.current.hover || held.current.focus
    if (now && !was) onPause()
    else if (!now && was) onResume()
  }
  const { icon: Icon, iconColor } = TONE[item.tone]
  const isError = item.tone === 'error'
  // Errors and durable results share the same treatment: fully wrapped,
  // selectable, and copyable. Both are text someone needs to act on rather
  // than glance at.
  const copyable = isError || item.durable
  return (
    <div
      role={isError ? 'alert' : undefined}
      onMouseEnter={() => hold('hover', true)}
      onMouseLeave={() => hold('hover', false)}
      onFocus={() => hold('focus', true)}
      onBlur={() => hold('focus', false)}
      className="pointer-events-auto flex items-start gap-2.5 rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-ink shadow-lg"
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {/* Wrap fully + stay selectable so a long message can be read/copied. */}
        <div className={copyable ? 'select-text whitespace-pre-wrap break-words' : ''}>
          {item.message}
        </div>
        {copyable ? (
          <div className="mt-1.5">
            <CopyButton text={item.message} />
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="-my-1 -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-mut hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red-dark"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}

interface DismissTimer {
  handle: number | null
  remaining: number
  startedAt: number
}

export function ToastProvider({
  children,
  bottomOffset,
}: {
  children: ReactNode
  /** CSS length for the gap above the viewport bottom; use it when a bottom nav
   *  would otherwise cover the toasts (default 1rem). Applies below the md
   *  breakpoint only, where the coach and referee bottom nav shows. */
  bottomOffset?: string
}) {
  const [items, setItems] = useState<ToastItem[]>([])
  const idRef = useRef(0)
  const itemsRef = useRef<ToastItem[]>([])
  const timers = useRef(new Map<number, DismissTimer>())

  const remove = useCallback((id: number) => {
    const t = timers.current.get(id)
    if (t?.handle != null) window.clearTimeout(t.handle)
    timers.current.delete(id)
    itemsRef.current = itemsRef.current.filter((x) => x.id !== id)
    setItems(itemsRef.current)
  }, [])

  const arm = useCallback(
    (id: number, ms: number) => {
      const entry = timers.current.get(id)
      if (!entry) return
      entry.remaining = ms
      entry.startedAt = Date.now()
      entry.handle = window.setTimeout(() => remove(id), ms)
    },
    [remove],
  )

  const pause = useCallback((id: number) => {
    const entry = timers.current.get(id)
    if (!entry || entry.handle == null) return
    window.clearTimeout(entry.handle)
    entry.handle = null
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt))
  }, [])

  const resume = useCallback(
    (id: number) => {
      const entry = timers.current.get(id)
      if (!entry || entry.handle != null) return
      arm(id, entry.remaining)
    },
    [arm],
  )

  useEffect(() => {
    const live = timers.current
    return () => {
      for (const t of live.values()) if (t.handle != null) window.clearTimeout(t.handle)
      live.clear()
    }
  }, [])

  const push = useCallback(
    (tone: ToastTone, message: string, durable = false) => {
      const id = (idRef.current += 1)
      // Cap the stack so a runaway loop of (non-dismissing) error toasts can't
      // accumulate without bound — keep the most recent few.
      const all = [...itemsRef.current, { id, tone, message, durable }]
      const next = all.slice(-8)
      for (const dropped of all.slice(0, -8)) {
        const t = timers.current.get(dropped.id)
        if (t?.handle != null) window.clearTimeout(t.handle)
        timers.current.delete(dropped.id)
      }
      itemsRef.current = next
      setItems(next)
      const ms = toastDurationMs(tone, durable)
      if (ms !== null) {
        timers.current.set(id, { handle: null, remaining: ms, startedAt: 0 })
        arm(id, ms)
      }
    },
    [arm],
  )

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, opts) => push('success', m, opts?.durable),
      error: (m) => push('error', m),
      info: (m, opts) => push('info', m, opts?.durable),
    }),
    [push],
  )

  const view = (t: ToastItem) => (
    <ToastView
      key={t.id}
      item={t}
      onClose={() => remove(t.id)}
      onPause={() => pause(t.id)}
      onResume={() => resume(t.id)}
    />
  )

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-4 bottom-[var(--toast-bottom,1rem)] z-[100] flex flex-col gap-2 md:bottom-4 md:left-auto md:right-4 md:w-full md:max-w-md"
        style={bottomOffset ? ({ '--toast-bottom': bottomOffset } as CSSProperties) : undefined}
      >
        {/* One polite region for acknowledgements, whose toasts carry no role of
            their own (a role="status" inside it would nest a second live
            region). Errors are role="alert" siblings outside it, so a screen
            reader never announces them twice. */}
        <div aria-live="polite" className="flex flex-col gap-2">
          {items.filter((t) => t.tone !== 'error').map(view)}
        </div>
        {items.filter((t) => t.tone === 'error').map(view)}
      </div>
    </ToastCtx.Provider>
  )
}
